import { formatDate, handleImageError } from "helpers/globalFunction";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef } from "react";
import Zoom from "react-medium-image-zoom";
import {
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

interface DetailModalQrAttendanceProps {
  show: boolean;
  onCloseClick: () => void;
  data: any;
}

const DetailModalQrAttendance: React.FC<DetailModalQrAttendanceProps> = ({
  show,
  onCloseClick,
  data,
}) => {
  const checkInMapRef = useRef<HTMLDivElement | null>(null);
  const checkOutMapRef = useRef<HTMLDivElement | null>(null);
  const checkInMapInstance = useRef<L.Map | null>(null);
  const checkOutMapInstance = useRef<L.Map | null>(null);

  delete (L.Icon.Default.prototype as any)._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  });

  const createCustomIcon = (color: any) => {
    return L.divIcon({
      html: `
        <div style="
          background-color: ${color};
          width: 30px;
          height: 30px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <i class="ri-map-pin-fill" style="
            transform: rotate(45deg);
            color: white;
            font-size: 16px;
          "></i>
        </div>
      `,
      className: "custom-marker",
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30],
    });
  };

  const initMap = (
    mapRef: React.MutableRefObject<HTMLDivElement | null>,
    mapInstance: React.MutableRefObject<L.Map | null>,
    location: any,
    title: string,
    color: string
  ) => {
    if (!location || !location.lat || !location.lon) return;

    const lat = parseFloat(location.lat);
    const lon = parseFloat(location.lon);

    if (isNaN(lat) || isNaN(lon)) {
      console.warn(`Koordinat tidak valid untuk ${title}:`, {
        lat: location.lat,
        lon: location.lon,
      });
      return;
    }

    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      console.warn(`Koordinat di luar jangkauan untuk ${title}:`, { lat, lon });
      return;
    }

    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }

    if (mapRef.current) {
      const map = L.map(mapRef.current).setView([lat, lon], 15);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      const customIcon = createCustomIcon(color);
      const marker = L.marker([lat, lon], { icon: customIcon }).addTo(map);

      marker
        .bindPopup(
          `
        <div style="max-width: 180px;">
          <h6 style="margin: 0 0 8px 0; color: ${color};">
            <i class="ri-map-pin-line"></i> ${title}
          </h6>
          <p style="margin: 0; font-size: 12px; color: #666;">
            ${location.address || "Alamat tidak tersedia"}
          </p>
          <p style="margin: 8px 0 0 0; font-size: 11px; color: #999;">
            <i class="ri-focus-3-line"></i> ${lat.toFixed(6)}, ${lon.toFixed(6)}
          </p>
        </div>
      `
        )
        .openPopup();

      L.circle([lat, lon], {
        color: color,
        fillColor: color,
        fillOpacity: 0.1,
        radius: 50,
      }).addTo(map);

      mapInstance.current = map;

      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }
  };

  useEffect(() => {
    if (show && data) {
      if (data.location) {
        setTimeout(() => {
          initMap(
            checkInMapRef,
            checkInMapInstance,
            data.location,
            "Check-In",
            "#0ab39c"
          );
        }, 300);
      }

      if (data.checkOutLocation) {
        setTimeout(() => {
          initMap(
            checkOutMapRef,
            checkOutMapInstance,
            data.checkOutLocation,
            "Check-Out",
            "#f06548"
          );
        }, 300);
      }
    }

    return () => {
      if (checkInMapInstance.current) {
        checkInMapInstance.current.remove();
        checkInMapInstance.current = null;
      }
      if (checkOutMapInstance.current) {
        checkOutMapInstance.current.remove();
        checkOutMapInstance.current = null;
      }
    };
  }, [show, data]);

  const getStatusBadge = (status: any) => {
    switch (status) {
      case "ON_TIME":
        return (
          <span className="badge bg-success fs-13 px-4 py-2 rounded-pill">
            <i className="ri-check-double-line me-1"></i>
            Tepat Waktu
          </span>
        );
      case "LATE":
        return (
          <span className="badge bg-danger fs-13 px-4 py-2 rounded-pill">
            <i className="ri-time-line me-1"></i>
            Terlambat
          </span>
        );
      case "TOLERANCE":
        return (
          <span className="badge bg-warning fs-13 px-4 py-2 rounded-pill">
            <i className="ri-alarm-warning-line me-1"></i>
            Toleransi
          </span>
        );
      default:
        return (
          <span className="badge bg-info fs-13 px-4 py-2 rounded-pill">
            <i className="ri-information-line me-1"></i>-
          </span>
        );
    }
  };

  const InfoItem = ({
    icon,
    label,
    value,
    iconColor = "primary",
  }: {
    icon: string;
    label: string;
    value: string | undefined;
    iconColor?: string;
  }) => (
    <div
      className="d-flex align-items-start p-3 rounded"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <div
        className={`flex-shrink-0 avatar-sm bg-${iconColor} bg-opacity-10 rounded-circle`}
      >
        <div className="avatar-title bg-transparent">
          <i className={`${icon} text-${iconColor} fs-18`}></i>
        </div>
      </div>
      <div className="flex-grow-1 ms-3">
        <p className="text-muted mb-1 fs-12 text-uppercase">{label}</p>
        <h6 className="mb-0 fs-15 fw-semibold">{value || "-"}</h6>
      </div>
    </div>
  );

  const AttendanceCard = ({
    title,
    time,
    location,
    mapRef,
    type,
  }: {
    title: string;
    time: string | undefined;
    location: any;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
    type: "checkIn" | "checkOut";
  }) => {
    const bgColor = type === "checkIn" ? "success" : "danger";
    const gradient =
      type === "checkIn"
        ? "linear-gradient(135deg, #0ab39c 0%, #0d9488 100%)"
        : "linear-gradient(135deg, #f06548 0%, #e63946 100%)";

    return (
      <Card
        className="shadow-sm mb-0 overflow-hidden"
        style={{ border: "none" }}
      >
        <div className="p-3" style={{ background: gradient }}>
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="text-white mb-0 fw-semibold fs-15">
              <i
                className={`ri-${
                  type === "checkIn" ? "login-box" : "logout-box"
                }-line me-2`}
              ></i>
              {title}
            </h6>
            <span className="badge bg-white text-dark fs-11 px-3 py-1">
              {type === "checkIn" ? "Masuk" : "Keluar"}
            </span>
          </div>
        </div>

        <CardBody className="p-3">
          {time ? (
            <div
              className="rounded-3 p-3 mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <div className="d-flex align-items-center">
                <div
                  className={`avatar-xs bg-${bgColor} bg-opacity-10 rounded-circle me-3`}
                >
                  <div className="avatar-title bg-transparent">
                    <i className={`ri-time-line text-${bgColor} fs-16`}></i>
                  </div>
                </div>
                <div>
                  <div className="fs-11 text-uppercase text-muted mb-1 fw-medium">
                    Waktu
                  </div>
                  <div className="fw-semibold fs-14">
                    {formatDate(time, "fullDateTime")}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="rounded-3 p-3 mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <div className="text-center py-2">
                <i
                  className={`ri-time-line text-${bgColor} opacity-50 fs-20`}
                ></i>
                <p className="text-muted mb-0 mt-2 fs-13">
                  Belum {type === "checkIn" ? "check-in" : "check-out"}
                </p>
              </div>
            </div>
          )}

          {location && location?.lat && location?.lon ? (
            <>
              <div
                ref={mapRef}
                className="rounded-3 overflow-hidden mb-3"
                style={{
                  height: "220px",
                  width: "100%",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              ></div>

              <div
                className="rounded-3 p-3"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <div className="d-flex align-items-start">
                  <div
                    className={`flex-shrink-0 avatar-xs bg-${bgColor} bg-opacity-10 rounded-circle me-2`}
                  >
                    <div className="avatar-title bg-transparent">
                      <i
                        className={`ri-map-pin-line text-${bgColor} fs-16`}
                      ></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <p className="text-uppercase text-muted mb-1 fs-11 fw-medium">
                      Lokasi
                    </p>
                    <p className="mb-1 fs-13 fw-medium">
                      {location.address || "-"}
                    </p>
                    <p className="text-muted mb-0 fs-11">
                      <i className="ri-focus-3-line me-1"></i>
                      {!isNaN(parseFloat(location.lat)) &&
                      !isNaN(parseFloat(location.lon))
                        ? `${parseFloat(location.lat).toFixed(6)}, ${parseFloat(
                            location.lon
                          ).toFixed(6)}`
                        : "Koordinat tidak valid"}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              className="rounded-3 p-3"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <div className="text-center py-3">
                <i
                  className={`ri-map-pin-line text-${bgColor} opacity-50 fs-2`}
                ></i>
                <p className="text-muted mb-0 mt-2 fs-13">
                  Lokasi tidak tersedia
                </p>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <Modal
      isOpen={show}
      toggle={onCloseClick}
      size="lg"
      centered
      className="modal-attendance-detail"
    >
      <ModalHeader toggle={onCloseClick} className="bg-white border-0">
        <div className="d-flex align-items-center">
          {/* <div className="avatar-sm bg-primary bg-opacity-10 rounded-circle me-3">
            <div className="avatar-title bg-transparent">
              <i className="ri-file-list-3-line fs-20 text-primary"></i>
            </div>
          </div> */}
          <span className="fs-18 fw-semibold">Detail Absensi</span>
        </div>
      </ModalHeader>
      <ModalBody className="p-4">
        {/* Header Card */}
        <Card
          className="mb-4 overflow-hidden shadow-sm"
          style={{
            border: "none",
            background: "linear-gradient(135deg, #405189 0%, #2f3d6b 100%)",
          }}
        >
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col lg={8}>
                <div className="d-flex align-items-center">
                  <div
                    className="avatar-lg rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                  >
                    <i className="ri-user-line text-white fs-24"></i>
                  </div>
                  <div>
                    <h4 className="text-white mb-2 fw-semibold">
                      {data?.member?.name || "-"}
                    </h4>
                    <div className="d-flex gap-2 flex-wrap">
                      <span
                        className="badge fs-12 px-3 py-2"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "white",
                        }}
                      >
                        <i className="ri-id-card-line me-1"></i>
                        {data?.member?.noId || "-"}
                      </span>
                      <span
                        className="badge fs-12 px-3 py-2"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "white",
                        }}
                      >
                        <i className="ri-briefcase-line me-1"></i>
                        {data?.shift?.name || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={4} className="text-lg-end mt-3 mt-lg-0">
                {getStatusBadge(data?.status)}
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="mb-4 shadow-sm" style={{ border: "none" }}>
          <CardBody className="p-4">
            <div className="d-flex align-items-center mb-4">
              <div className="avatar-sm bg-info bg-opacity-10 rounded-circle me-3">
                <div className="avatar-title bg-transparent">
                  <i className="ri-timer-line text-info fs-20"></i>
                </div>
              </div>
              <h5 className="mb-0 fw-semibold">Informasi Shift</h5>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <InfoItem
                  icon="ri-calendar-event-line"
                  label="Nama Shift"
                  value={data?.shift?.name}
                  iconColor="info"
                />
              </Col>
              <Col md={6}>
                <InfoItem
                  icon="ri-time-line"
                  label="Jam Shift"
                  value={
                    data?.shift?.checkInTime && data?.shift?.checkOutTime
                      ? `${data.shift.checkInTime.slice(
                          0,
                          5
                        )} - ${data.shift.checkOutTime.slice(0, 5)}`
                      : "-"
                  }
                  iconColor="info"
                />
              </Col>
              <Col md={6}>
                <InfoItem
                  icon="ri-history-line"
                  label="Toleransi Keterlambatan"
                  value={
                    data?.shift?.tolerance
                      ? `${data.shift.tolerance} menit`
                      : "-"
                  }
                  iconColor="warning"
                />
              </Col>
              <Col md={6}>
                <InfoItem
                  icon="ri-calendar-check-line"
                  label="Tanggal Absensi"
                  value={formatDate(data?.checkInTime, "fullDateTime")}
                  iconColor="success"
                />
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Row className="g-4">
          <Col lg={6}>
            <AttendanceCard
              title="Data Check-In"
              time={data?.checkInTime}
              location={data?.location}
              mapRef={checkInMapRef}
              type="checkIn"
            />
          </Col>
          <Col lg={6}>
            <AttendanceCard
              title="Data Check-Out"
              time={data?.checkOutTime}
              location={data?.checkOutLocation}
              mapRef={checkOutMapRef}
              type="checkOut"
            />
          </Col>
        </Row>

        <div className="text-end mt-4 pt-3">
          <button
            type="button"
            className="btn btn-light px-4 btn-sm"
            onClick={onCloseClick}
          >
            Tutup
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default DetailModalQrAttendance;
